// Phase 3 commands: trips, containers, the port, exceptions, holds, alerts, advances (04 §4.4, §4.5c, §4.6).
import { z } from 'zod';
import { defineCommand } from '../define';
import { branchCodeSchema } from '../config';
import { barcodeSchema, trackingNoSchema } from '../cargo';
import { DOCUMENT_SUBJECT_KINDS, DOCUMENT_TYPES } from '../documents';
import { CUSTOMS_STATUSES, EXCEPTION_TYPES, HOLD_TYPES, freeTimeSchema } from '../movement';

const minor = z.number().int().nonnegative().max(1e12);
const barcodes = z.array(barcodeSchema).min(1).max(200);
const id = z.string().min(1).max(40);
const at = z.iso.datetime({ offset: true });

// ---- Trips ---------------------------------------------------------------------

export const openTrip = defineCommand({
  type: 'openTrip',
  permission: 'trip.load',
  principals: [],
  offline: true,
  payload: z
    .object({
      tripId: z.string().regex(/^trp_[0-9A-HJKMNP-TV-Z]{26}$/),
      toBranchId: branchCodeSchema,
      vehicleId: z.string().max(80).optional(),
      driverName: z.string().max(80).default(''),
    })
    .strict(),
});
export const loadTrip = defineCommand({
  type: 'loadTrip',
  permission: 'trip.load',
  principals: [],
  offline: true,
  payload: z.object({ tripId: id, barcodes }).strict(),
});
export const unloadFromTrip = defineCommand({
  type: 'unloadFromTrip',
  permission: 'trip.load',
  principals: [],
  offline: true,
  payload: z.object({ tripId: id, barcodes }).strict(),
});
export const departTrip = defineCommand({
  type: 'departTrip',
  permission: 'trip.load',
  principals: [],
  offline: true,
  payload: z
    .object({ tripId: id, costMinor: minor.optional(), departedAt: at.optional() })
    .strict(),
});
export const receiveTrip = defineCommand({
  type: 'receiveTrip',
  permission: 'trip.unload',
  principals: [],
  offline: true,
  payload: z.object({ tripId: id, barcodes }).strict(),
});
export const closeTrip = defineCommand({
  type: 'closeTrip',
  permission: 'trip.unload',
  principals: [],
  offline: true,
  payload: z.object({ tripId: id, note: z.string().max(300).default('') }).strict(),
});

// ---- Containers -----------------------------------------------------------------

export const planContainer = defineCommand({
  type: 'planContainer',
  permission: 'container.manage',
  principals: [],
  offline: false,
  payload: z
    .object({
      containerId: z.string().regex(/^ctn_[0-9A-HJKMNP-TV-Z]{26}$/),
      kind: z.enum(['sea_fcl', 'sea_lcl', 'air']),
      containerType: z.enum(['40hc', '40ft', '20ft', 'lcl', 'air']),
      importHubId: branchCodeSchema,
      carrier: z.string().max(80).default(''),
      bookingNo: z.string().max(40).default(''),
      vessel: z.string().max(80).default(''),
      voyage: z.string().max(40).default(''),
      polPortId: z.string().max(10).default(''),
      podPortId: z.string().max(10).default('PHMNL'),
      etd: z.iso.date().optional(),
      eta: z.iso.date().optional(),
      freeTime: freeTimeSchema.optional(),
    })
    .strict(),
});
export const updateContainer = defineCommand({
  type: 'updateContainer',
  permission: 'container.manage',
  principals: [],
  offline: false,
  payload: z
    .object({
      containerId: id,
      containerNo: z.string().max(20).optional(),
      blNo: z.string().max(40).optional(),
      carrier: z.string().max(80).optional(),
      vessel: z.string().max(80).optional(),
      voyage: z.string().max(40).optional(),
      etd: z.iso.date().optional(),
      eta: z.iso.date().optional(),
      freeTime: freeTimeSchema.optional(),
    })
    .strict(),
});
export const recordEmptyPickup = defineCommand({
  type: 'recordEmptyPickup',
  permission: 'container.manage',
  principals: [],
  offline: true,
  payload: z.object({ containerId: id, containerNo: z.string().max(20), at }).strict(),
});
export const loadContainer = defineCommand({
  type: 'loadContainer',
  permission: 'container.load',
  principals: [],
  offline: true,
  payload: z.object({ containerId: id, barcodes }).strict(),
});
export const removeFromContainer = defineCommand({
  type: 'removeFromContainer',
  permission: 'container.load',
  principals: [],
  offline: true,
  payload: z.object({ containerId: id, barcodes }).strict(),
});
export const closeContainer = defineCommand({
  type: 'closeContainer',
  permission: 'container.manage',
  principals: [],
  offline: true,
  payload: z.object({ containerId: id, sealNo: z.string().min(1).max(30) }).strict(),
});
export const gateInAtPort = defineCommand({
  type: 'gateInAtPort',
  permission: 'container.manage',
  principals: [],
  offline: true,
  payload: z.object({ containerId: id, at }).strict(),
});
export const exportContainer = defineCommand({
  type: 'exportContainer',
  permission: 'container.manage',
  principals: [],
  offline: false,
  payload: z
    .object({ containerId: id, atd: at, directorOverrideReason: z.string().max(300).optional() })
    .strict(),
});
export const releaseBL = defineCommand({
  type: 'releaseBL',
  permission: 'container.manage',
  principals: [],
  offline: false,
  payload: z.object({ containerId: id, at, reference: z.string().max(60).default('') }).strict(),
});
export const containerArrived = defineCommand({
  type: 'containerArrived',
  permission: 'container.manage',
  principals: [],
  offline: false,
  payload: z.object({ containerId: id, ata: at, dischargedAt: at.optional() }).strict(),
});
export const updateCustoms = defineCommand({
  type: 'updateCustoms',
  permission: 'customs.manage',
  principals: [],
  offline: false,
  payload: z
    .object({
      containerId: id,
      status: z.enum(CUSTOMS_STATUSES),
      at,
      entryNo: z.string().max(40).optional(),
      notes: z.string().max(500).default(''),
    })
    .strict(),
});
export const pullOutContainer = defineCommand({
  type: 'pullOutContainer',
  permission: 'container.manage',
  principals: [],
  offline: true,
  payload: z.object({ containerId: id, at }).strict(),
});
export const arriveAtHub = defineCommand({
  type: 'arriveAtHub',
  permission: 'trip.unload',
  principals: [],
  offline: true,
  payload: z.object({ containerId: id, at }).strict(),
});
export const receiveContainer = defineCommand({
  type: 'receiveContainer',
  permission: 'trip.unload',
  principals: [],
  offline: true,
  payload: z.object({ containerId: id, barcodes }).strict(),
});
export const completeContainerReceipt = defineCommand({
  type: 'completeContainerReceipt',
  permission: 'trip.unload',
  principals: [],
  offline: true,
  payload: z.object({ containerId: id, note: z.string().max(300).default('') }).strict(),
});
export const returnEmptyContainer = defineCommand({
  type: 'returnEmptyContainer',
  permission: 'container.manage',
  principals: [],
  offline: true,
  payload: z
    .object({
      containerId: id,
      at,
      depot: z.string().max(80).default(''),
      receiptPath: z.string().max(200).optional(),
    })
    .strict(),
});

// ---- Exceptions, holds, alerts -------------------------------------------------

export const raiseException = defineCommand({
  type: 'raiseException',
  permission: 'exception.raise',
  principals: [],
  offline: true,
  payload: z
    .object({
      exceptionId: z.string().regex(/^exc_[0-9A-HJKMNP-TV-Z]{26}$/),
      type: z.enum(EXCEPTION_TYPES),
      subject: z.object({ kind: z.enum(['box', 'shipment', 'trip', 'container']), id }).strict(),
      description: z.string().min(1).max(1000),
      photos: z.array(z.string().max(200)).max(10).default([]),
    })
    .strict(),
});
export const resolveException = defineCommand({
  type: 'resolveException',
  permission: 'exception.resolve',
  principals: [],
  offline: true,
  payload: z
    .object({
      exceptionId: id,
      action: z.enum(['found', 'written_off', 'repacked', 'closed']),
      note: z.string().min(1).max(1000),
      foundAtBranchId: branchCodeSchema.optional(),
    })
    .strict(),
});
export const placeHold = defineCommand({
  type: 'placeHold',
  permission: 'cargo.hold',
  principals: [],
  offline: true,
  payload: z
    .object({
      shipmentId: trackingNoSchema,
      type: z.enum(HOLD_TYPES),
      reason: z.string().min(1).max(300),
    })
    .strict(),
});
export const releaseHold = defineCommand({
  type: 'releaseHold',
  permission: 'cargo.hold',
  principals: [],
  offline: true,
  payload: z
    .object({
      shipmentId: trackingNoSchema,
      type: z.enum(HOLD_TYPES),
      note: z.string().max(300).default(''),
    })
    .strict(),
});
export const acknowledgeAlert = defineCommand({
  type: 'acknowledgeAlert',
  permission: 'alerts.respond',
  principals: [],
  offline: true,
  payload: z.object({ alertId: id, actionNote: z.string().min(3).max(500) }).strict(),
});
export const reassignAlert = defineCommand({
  type: 'reassignAlert',
  permission: 'alerts.manage',
  principals: [],
  offline: false,
  payload: z.object({ alertId: id, assigneeUid: z.string().min(1).max(128) }).strict(),
});

// ---- Money between countries ---------------------------------------------------

export const fundAdvance = defineCommand({
  type: 'fundAdvance',
  permission: 'finance.intercompany',
  principals: [],
  offline: false,
  payload: z
    .object({
      advanceId: id,
      fromWalletId: id,
      toWalletId: id,
      sentMinor: minor,
      receivedMinor: minor,
      note: z.string().max(300).default(''),
    })
    .strict(),
});

/**
 * Settles what an intercompany invoice left open after its advance (14 §14.9): the origin pays what
 * it still owes, or the destination refunds the part of the advance it did not use. The side that
 * owes sends; both countries post, the FX difference on 5200. Always waits for a checker (14 §14.10).
 */
/**
 * Staging and local only (owner's decision 2026-10-30): undo the intercompany invoice of a TEST
 * container, e.g. one raised from a wrong cost model. Each of its journals gets a reversing journal
 * in its own country's books; the invoice is kept and marked reversed. Refused once anything was
 * settled on it, and for a container whose number or booking does not carry TEST.
 */
export const reverseTestIntercompanyInvoice = defineCommand({
  type: 'reverseTestIntercompanyInvoice',
  permission: 'dev.tools',
  principals: [],
  offline: false,
  devOnly: true,
  payload: z
    .object({
      invoiceId: id,
      reason: z.string().trim().min(3).max(300),
    })
    .strict(),
});

/**
 * Staging and local only (owner's decision 2026-10-31, R11): moves the dates of a TEST container that
 * has not left yet, and of the shipments in it, `days` back, so a test voyage can have a real-looking
 * departure and an arrival up to today. Only the timeline dates move (booking, collection, the
 * milestones, the container's dates and loading times); scans, payments and journals keep theirs.
 */
export const shiftTestRun = defineCommand({
  type: 'shiftTestRun',
  permission: 'dev.tools',
  principals: [],
  offline: false,
  devOnly: true,
  payload: z
    .object({
      containerId: id,
      days: z.number().int().min(1).max(60),
      reason: z.string().trim().min(3).max(300),
    })
    .strict(),
});

export const settleIntercompany = defineCommand({
  type: 'settleIntercompany',
  permission: 'finance.intercompany',
  principals: [],
  offline: false,
  payload: z
    .object({
      invoiceId: id,
      /** The paying country's wallet (the origin's when it owes, the destination's for a refund). */
      fromWalletId: id,
      toWalletId: id,
      /** How much of the open balance this settles, in the destination's currency. */
      amountMinor: minor,
      sentMinor: minor,
      receivedMinor: minor,
      note: z.string().max(300).default(''),
    })
    .strict(),
});

/** Why money moves between two countries outside a container advance (14 §14.5). */
export const TRANSFER_PURPOSE_KEYS = [
  'working_capital',
  'cost_cover',
  'loan',
  'repayment',
  'other',
] as const;

/**
 * A general transfer between two countries' wallets (06 §6.3 `intercompanyTransfer`): funding,
 * a loan or a repayment, with its purpose. One command, both legs; always waits for a checker.
 */
export const intercompanyTransfer = defineCommand({
  type: 'intercompanyTransfer',
  permission: 'finance.intercompany',
  principals: [],
  offline: false,
  payload: z
    .object({
      fromWalletId: id,
      toWalletId: id,
      sentMinor: minor,
      receivedMinor: minor,
      purpose: z.enum(TRANSFER_PURPOSE_KEYS),
      reason: z.string().trim().min(3).max(300),
    })
    .strict(),
});

/**
 * Recomputes a container's actual destination costs and recharges the difference since its
 * intercompany invoice (14 §14.9, recharge basis `actual`): late bills, partner forwarding.
 */
export const trueUpRecharge = defineCommand({
  type: 'trueUpRecharge',
  permission: 'finance.intercompany',
  // The destination's accountant keeps its recharges right; it moves no money.
  alsoPermitted: ['finance.planning'],
  principals: [],
  offline: false,
  payload: z.object({ invoiceId: id }).strict(),
});

/**
 * Attaches an uploaded file (a PDF or a picture) to a container or a shipment as a named document
 * (04 §4.4). A container's documents need `container.manage` or `customs.manage`; a shipment's need
 * `cargo.amend`. The handler enforces that per subject, the gate only needs one of the three.
 */
export const attachDocument = defineCommand({
  type: 'attachDocument',
  permission: 'container.manage',
  alsoPermitted: ['customs.manage', 'cargo.amend'],
  principals: [],
  offline: false,
  payload: z
    .object({
      subject: z
        .object({ kind: z.enum(DOCUMENT_SUBJECT_KINDS), id: z.string().min(1).max(40) })
        .strict(),
      docType: z.enum(DOCUMENT_TYPES),
      /** The uploaded file: `uploads/{your uid}/{file}`. */
      path: z.string().min(1).max(200),
      title: z.string().trim().max(120).optional(),
      note: z.string().trim().max(300).optional(),
      /** A number printed on the document: the BL number, the invoice number, the entry number. */
      reference: z.string().trim().max(60).optional(),
      amountMinor: z.number().int().nonnegative().max(1e12).optional(),
      currency: z
        .string()
        .regex(/^[A-Z]{3}$/)
        .optional(),
    })
    .strict(),
});

export const MOVEMENT_COMMANDS = {
  openTrip,
  loadTrip,
  unloadFromTrip,
  departTrip,
  receiveTrip,
  closeTrip,
  planContainer,
  updateContainer,
  recordEmptyPickup,
  loadContainer,
  removeFromContainer,
  closeContainer,
  gateInAtPort,
  exportContainer,
  releaseBL,
  containerArrived,
  updateCustoms,
  pullOutContainer,
  arriveAtHub,
  receiveContainer,
  completeContainerReceipt,
  returnEmptyContainer,
  raiseException,
  resolveException,
  placeHold,
  releaseHold,
  acknowledgeAlert,
  reassignAlert,
  fundAdvance,
  settleIntercompany,
  intercompanyTransfer,
  trueUpRecharge,
  attachDocument,
} as const;
